import { useEffect, useState } from "react";
import {
  Loader2,
  BookOpen,
  Highlighter,
  StickyNote,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import GrowthChart from "./GrowthChart";
import DonationsChart from "./DonationsChart";
import ClickSourcesChart from "./ClickSourcesChart";

/**
 * Sprint B — slimmed Stats tab.
 *
 * The headline metrics that used to live here (total users, donations,
 * referral clicks) now live in the Story Header at the top of the page.
 * This tab keeps only the engagement metrics the Story Header doesn't
 * surface — lessons built, notes, highlights — plus the demo-data
 * cleanup control, which is still useful before going live.
 */

const REFRESH_MS = 30000;

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="rounded-md border border-border/60 bg-card/40 p-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
        <Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
        {label}
      </div>
      <div className="font-heading text-3xl text-foreground">{value}</div>
    </div>
  );
}

export default function AdminStatsTab() {
  const [stats, setStats] = useState(null);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const loadStats = () => {
    api.adminStats().then(setStats).catch(() => setStats({}));
  };

  useEffect(() => {
    loadStats();
    const t = setInterval(loadStats, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const res = await api.adminCleanupDemoData();
      toast.success("הניקוי הושלם", {
        description: `נמחקו ${res.users_deleted} משתמשי דמו, ${res.donations_deleted} תרומות דמו`,
      });
      setConfirmCleanup(false);
      loadStats();
    } catch (err) {
      toast.error("שגיאה בניקוי", {
        description: err.response?.data?.detail || "נסה שוב",
      });
    } finally {
      setCleaning(false);
    }
  };

  if (!stats) {
    return (
      <div className="grid place-items-center py-20" data-testid="admin-stats-loading">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-stats">
      {/* Sprint B Batch 3 — visual headliners. These two charts are the
          first thing the admin sees inside the סקירה tab and carry
          the story of growth + donations at a glance. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GrowthChart />
        <DonationsChart />
      </div>

      <ClickSourcesChart />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-sm tracking-[0.25em] uppercase text-gold">
            מדדי לימוד
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            תוכן שיצרו המשתמשים — מטריקות הליבה לתוריא חיה.
          </p>
        </div>
        <Button
          onClick={() => setConfirmCleanup(true)}
          variant="outline"
          size="sm"
          className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
          data-testid="admin-cleanup-demo-btn"
        >
          <Trash2 className="h-4 w-4 ml-1.5" strokeWidth={1.5} />
          נקה נתוני דמו
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi
          icon={BookOpen}
          label="שיעורים שנבנו"
          value={stats.engagement?.lessons_built ?? 0}
          testid="kpi-lessons"
        />
        <Kpi
          icon={StickyNote}
          label="הערות במחברת"
          value={stats.engagement?.notes ?? 0}
          testid="kpi-notes"
        />
        <Kpi
          icon={Highlighter}
          label="הדגשות בטקסט"
          value={stats.engagement?.highlights ?? 0}
          testid="kpi-highlights"
        />
      </div>

      <AlertDialog open={confirmCleanup} onOpenChange={setConfirmCleanup}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              לאשר ניקוי נתוני דמו?
            </AlertDialogTitle>
            <AlertDialogDescription>
              פעולה זו תמחק רק חשבונות שזוהו כדמו (אימייל ב-@test.com /
              @example.com או שמות בדיקה מהצוות).
              <br />
              <strong>החשבונות שלך לא ימחקו.</strong> תרומות אמיתיות
              נשארות.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>בטל</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCleanup}
              disabled={cleaning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : "מחק"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
